import type { components } from '../api/schema';
import { localeIntl, t } from './lingua';
import { nomeColonna, titoloDi } from './permessi';

/**
 * Le parole con cui il pannello racconta il registro.
 *
 * L'hub scrive codici — `server.provision.retry`, `instance.restore_in_place`
 * — e uno snapshot di prima e dopo. Chi apre il registro vuole leggere «ha
 * riavviato l'installazione del server «sandbox» dal passo 23»: il codice e
 * gli snapshot restano nel dettaglio, per chi deve ricostruire. Un codice
 * che qui non c'è si mostra com'è, mai come una riga vuota.
 */

export type Voce = components['schemas']['Voce'];

/** Lo snapshot prima o dopo, già decodificato; vuoto se manca o non è JSON. */
export function snapshot(json: string | undefined): Record<string, unknown> {
  if (!json) return {};
  try {
    const v: unknown = JSON.parse(json);
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/** La prima chiave presente, come stringa: il nome di quasi tutto sta in una di queste. */
function campo(v: Voce, ...chiavi: string[]): string {
  const dopo = snapshot(v.after);
  const prima = snapshot(v.before);
  for (const k of chiavi) {
    const x = dopo[k] ?? prima[k];
    if (typeof x === 'string' && x) return x;
    if (typeof x === 'number') return String(x);
  }
  return '';
}

/**
 * Chi, e tramite cosa. Dal pannello basta la persona; con un token si dice
 * quale, e se era un assistente lo si dice prima di tutto: è la prima cosa
 * che chi legge vuole sapere di un riavvio alle tre di notte.
 */
export function chiVoce(v: Voce): string {
  if (v.canale === 'mcp') {
    return v.via
      ? t('{chi} · tramite l’assistente, token {token}', { chi: v.actor, token: virgolette(v.via) })
      : t('{chi} · tramite l’assistente', { chi: v.actor });
  }
  if (v.via) return t('{chi} · con il token {token}', { chi: v.actor, token: virgolette(v.via) });
  return v.actor;
}

/** Il nome dell'oggetto a cui la riga si riferisce: «sandbox», «shop.acme.it», o l'inizio dell'id. */
export function nomeVoce(v: Voce): string {
  return campo(v, 'name', 'hostname', 'label', 'email', 'server', 'slug', 'domain', 'url')
    || (v.resourceId ? v.resourceId.slice(0, 8) : '');
}

function ruolo(r: string): string {
  const nomi: Record<string, string> = {
    owner: t('proprietario'), admin: t('amministratore'), operator: t('operatore'), member: t('membro'),
    viewer: t('lettore'), limited: t('limitato'),
  };
  return nomi[r] ?? r;
}

const provider = (p: string) => (p === 'ovh' ? 'OVH' : p === 'custom_ssh' ? t('una macchina collegata a mano') : p);

const virgolette = (s: string) => (s ? t('«{s}»', { s }) : '');

/**
 * Una frase per codice. Riceve la voce e il nome già estratto; `n` è il
 * nome fra virgolette, o vuoto se non c'è, così la frase non resta con
 * due virgolette e niente in mezzo.
 */
const FRASI: Record<string, (v: Voce, n: string) => string> = {
  'server.create': (v, n) => {
    const p = campo(v, 'provider');
    return p && p !== 'custom_ssh'
      ? t('Ha creato il server {n} su {provider}', { n, provider: provider(p) })
      : t('Ha collegato il server {n}', { n });
  },
  'server.delete': (_, n) => t('Ha scollegato il server {n}', { n }),
  'server.provision.retry': (v, n) => {
    const da = campo(v, 'from_step');
    return da && da !== '0'
      ? t('Ha riavviato l’installazione del server {n} dal passo {passo}', { n, passo: da })
      : t('Ha riavviato l’installazione del server {n}', { n });
  },
  'server.bootstrap.fetch': (_, n) => t('Il server {n} ha scaricato lo script di avvio', { n }),
  'migration.source.create': (_, n) => t('Ha collegato la macchina CloudPepper {n} come sorgente di trasloco', { n }),
  'migration.source.bootstrap.fetch': (_, n) => t('La macchina CloudPepper {n} ha scaricato lo script di collegamento', { n }),
  'migration.source.connect': (_, n) => t('Molo è entrato nella macchina CloudPepper {n}', { n }),
  'migration.create': (v, n) => t('Ha aperto il trasloco di {istanza} da {sorgente} verso {server}', {
    istanza: campo(v, 'source_instance') || n,
    sorgente: virgolette(campo(v, 'source')),
    server: virgolette(campo(v, 'server')),
  }),
  'migration.update': (_, n) => t('Ha corretto il trasloco {n}', { n }),
  'migration.trial': (_, n) => t('Ha fatto partire la prova del trasloco {n}', { n }),
  'migration.cutover': (_, n) => t('Ha fatto partire il trasloco vero di {n}: CloudPepper si spegne', { n }),
  'migration.revert': (_, n) => t('È tornato su CloudPepper con {n}', { n }),
  'migration.addresses.done': (_, n) => t('Ha finito gli indirizzi del trasloco {n}', { n }),
  'migration.confirm': (_, n) => t('Ha dato le conferme del trasloco {n}', { n }),
  'migration.done': (_, n) => t('Ha chiuso il trasloco {n}', { n }),
  'migration.cancel': (_, n) => t('Ha annullato il trasloco {n}', { n }),
  'instance.import': (v, n) =>
    snapshot(v.after).definitivo === true
      ? t('Ha importato {n} da CloudPepper, senza neutralizzarla', { n })
      : t('Ha importato {n} da CloudPepper, come prova neutralizzata', { n }),
  'instance.addons.webhook': (v) => t('Ha rifatto il segreto del webhook di {url}', { url: campo(v, 'url') }),
  'migration.source.disconnect': (v, n) =>
    snapshot(v.after).forget === true
      ? t('Ha dimenticato la macchina CloudPepper {n}, senza toccarla', { n })
      : t('Ha scollegato la macchina CloudPepper {n}', { n }),
  'server.reboot': (_, n) => t('Ha riavviato il server {n}', { n }),
  'agent.enrol': (v) => {
    const host = campo(v, 'hostname');
    return host ? t('L’agente si è registrato da {host}', { host }) : t('L’agente si è registrato');
  },
  'ip.assign': (v) => t('Ha assegnato l’IP {ip} al server {server}', { ip: campo(v, 'ip'), server: virgolette(campo(v, 'server')) }),
  'ip.release': (v) => t('Ha liberato l’IP {ip} dal server {server}', { ip: campo(v, 'ip'), server: virgolette(campo(v, 'server')) }),
  'ip.buy': (v) => t('Ha comprato un IP fisso per il server {server}', { server: virgolette(campo(v, 'server')) }),
  'dr.server_lost': (v, n) => {
    const d = snapshot(v.after);
    const frase = typeof d.dest_server === 'string'
      ? t('Ha dichiarato perso il server {n} e ripristinato le istanze su {dest}', { n, dest: virgolette(d.dest_server) })
      : t('Ha dichiarato perso il server {n}', { n });
    return d.exercise === true ? `${frase} ${t('(esercitazione)')}` : frase;
  },

  'instance.create': (v, n) => {
    const major = campo(v, 'odoo_major');
    return major ? t('Ha creato l’istanza {n} con Odoo {major}', { n, major }) : t('Ha creato l’istanza {n}', { n });
  },
  'instance.delete': (_, n) => t('Ha rimosso l’istanza {n}', { n }),
  'instance.start': (_, n) => t('Ha avviato l’istanza {n}', { n }),
  'instance.stop': (_, n) => t('Ha fermato l’istanza {n}', { n }),
  'instance.restart': (_, n) => t('Ha riavviato l’istanza {n}', { n }),
  'instance.restore': (_, n) => t('Ha ripristinato un backup nella nuova istanza {n}', { n }),
  'instance.restore_in_place': (_, n) => t('Ha ripristinato un backup sull’istanza {n}', { n }),
  'instance.login_as': (v, n) => t('È entrato in {n} come {utente}', { n, utente: campo(v, 'odoo_login') || t('un utente') }),
  'instance.update': () => t('Ha aggiornato il codice di Odoo dell’istanza'),
  'server.ssh_access.grant': (v, n) => {
    const valori = { email: campo(v, 'email'), n, ore: campo(v, 'hours') };
    const motivo = campo(v, 'reason');
    return motivo
      ? t('Ha dato a {email} l’accesso SSH a {n} per {ore} ore: {motivo}', { ...valori, motivo })
      : t('Ha dato a {email} l’accesso SSH a {n} per {ore} ore', valori);
  },
  'server.ssh_access.revoke': (v, n) => t('Ha revocato l’accesso SSH n. {serial} a {n}', { serial: campo(v, 'serial'), n }),
  'instance.modules': (v, n) => {
    const d = snapshot(v.after);
    const lista = (k: string) => (Array.isArray(d[k]) ? (d[k] as unknown[]).map(String).join(', ') : '');
    const installati = lista('install');
    const aggiornati = lista('update');
    if (installati && aggiornati === 'all') return t('Ha installato {installati} e aggiornato tutti i moduli in {n}', { installati, n });
    if (installati && aggiornati) return t('Ha installato {installati} e aggiornato {aggiornati} in {n}', { installati, aggiornati, n });
    if (installati) return t('Ha installato {installati} in {n}', { installati, n });
    if (aggiornati === 'all') return t('Ha aggiornato tutti i moduli in {n}', { n });
    if (aggiornati) return t('Ha aggiornato {aggiornati} in {n}', { aggiornati, n });
    return t('Ha lavorato sui moduli in {n}', { n });
  },
  'instance.python_packages': (v, n) => {
    const d = snapshot(v.after);
    const lista = Array.isArray(d.packages) ? (d.packages as unknown[]).map(String).join(', ') : '';
    return lista ? t('Ha impostato le librerie Python di {n}: {lista}', { n, lista }) : t('Ha tolto le librerie Python di {n}', { n });
  },
  'instance.config.write': () => t('Ha cambiato la configurazione dell’istanza'),
  'instance.enterprise.enable': () => t('Ha attivato Enterprise sull’istanza'),
  'instance.addons.link': (v) => {
    const url = campo(v, 'url');
    const ramo = campo(v, 'branch');
    return ramo ? t('Ha collegato il repository {url} ({ramo})', { url, ramo }) : t('Ha collegato il repository {url}', { url });
  },
  'instance.addons.unlink': (v) => t('Ha scollegato il repository {url}', { url: campo(v, 'url') }),
  'instance.addons.branch': (v) => {
    const prima = snapshot(v.before).branch;
    const dopo = snapshot(v.after).branch;
    const valori = { url: campo(v, 'url'), ramo: typeof dopo === 'string' ? dopo : '?' };
    return typeof prima === 'string'
      ? t('Ha spostato {url} sul ramo {ramo} (era {prima})', { ...valori, prima })
      : t('Ha spostato {url} sul ramo {ramo}', valori);
  },
  'instance.addons.push': (v) => t('Push su {url}: moduli aggiornati', { url: campo(v, 'url') }),
  'staging.create': (v, n) => {
    const da = campo(v, 'from');
    return da ? t('Ha creato lo staging {n} da {da}', { n, da: virgolette(da) }) : t('Ha creato lo staging {n}', { n });
  },
  'staging.refresh': () => t('Ha aggiornato lo staging con i dati e il codice di produzione'),
  'domain.create': (v) => t('Ha collegato il dominio {dominio}', { dominio: campo(v, 'hostname') }),
  'domain.delete': (v) => t('Ha scollegato il dominio {dominio}', { dominio: campo(v, 'hostname') }),
  'domain.retry': (v) => t('Ha ritentato il certificato di {dominio}', { dominio: campo(v, 'hostname') }),

  'backup.create': (_, n) => t('Ha fatto un backup dell’istanza {n}', { n }),
  'backup.test_restore': (_, n) => t('Ha provato il ripristino dell’istanza {n}', { n }),
  'backup.download': (v) => {
    const db = campo(v, 'db_name');
    return db ? t('Ha scaricato un backup ({db})', { db }) : t('Ha scaricato un backup');
  },

  'invitation.create': (v) => t('Ha invitato {email} come {ruolo}', { email: campo(v, 'email'), ruolo: ruolo(campo(v, 'role')) }),
  'invitation.revoke': (v) => t('Ha annullato l’invito a {email}', { email: campo(v, 'email') }),
  'invitation.accept': (v) => t('Ha accettato l’invito ed è entrato come {ruolo}', { ruolo: ruolo(campo(v, 'role')) }),
  'member.remove': () => t('Ha tolto una persona dall’organizzazione'),
  'member.role.change': (v) => {
    const prima = snapshot(v.before).role;
    const dopo = snapshot(v.after).role;
    return typeof prima === 'string' && typeof dopo === 'string'
      ? t('Ha cambiato un ruolo: da {prima} a {dopo}', { prima: ruolo(prima), dopo: ruolo(dopo) })
      : t('Ha cambiato un ruolo');
  },
  'grant.create': (v) => {
    const r = campo(v, 'role');
    return r
      ? t('Ha concesso un accesso a una singola risorsa ({ruolo})', { ruolo: ruolo(r) })
      : t('Ha concesso un accesso a una singola risorsa');
  },
  'grant.revoke': () => t('Ha revocato un accesso a una singola risorsa'),
  'token.create': (v) => t('Ha creato il token API {nome}', { nome: virgolette(campo(v, 'label')) }),
  'token.revoke': (v) => t('Ha revocato il token API {nome}', { nome: virgolette(campo(v, 'label')) }),
  'impersonation.start': () => t('Ha iniziato a guardare con gli occhi di un altro utente'),
  'impersonation.end': () => t('Ha smesso di guardare con gli occhi di un altro utente'),

  'org.create': (_, n) => t('Ha creato l’organizzazione {n}', { n }),
  'org.rename': (v) => t('Ha rinominato l’organizzazione in {nome}', { nome: virgolette(campo(v, 'name')) }),
  'mcp.enable': () => t('Ha acceso gli assistenti'),
  'mcp.connect': (v) => t('Ha collegato l’assistente {client}', { client: virgolette(campo(v, 'client')) }),
  'mcp.disconnect': (v) => t('Ha revocato il collegamento di {client}', { client: virgolette(campo(v, 'client')) }),
  'mcp.disable': () => t('Ha spento gli assistenti'),
  'mcp.tool.enable': (v) => t('Ha acceso per gli assistenti {tool}', { tool: virgolette(campo(v, 'title', 'tool')) }),
  'mcp.tool.disable': (v) => t('Ha spento per gli assistenti {tool}', { tool: virgolette(campo(v, 'title', 'tool')) }),
  'mcp.tool.admins.enable': (v) => t('Ha concesso agli admin, per gli assistenti, {tool}', { tool: virgolette(campo(v, 'title', 'tool')) }),
  'mcp.tool.admins.disable': (v) => t('Ha tolto agli admin, per gli assistenti, {tool}', { tool: virgolette(campo(v, 'title', 'tool')) }),
  // La matrice dello staff (blocco 35): vale per tutte, e la riga c'è in ognuna.
  'permessi.matrice.cambia': (v) => {
    const valori = { permesso: virgolette(t(titoloDi(campo(v, 'permesso')))), colonna: nomeColonna(campo(v, 'colonna')) };
    return snapshot(v.after).concesso === true
      ? t('Ha concesso {permesso} a {colonna}, in tutte le organizzazioni', valori)
      : t('Ha tolto {permesso} a {colonna}, in tutte le organizzazioni', valori);
  },
  'permessi.matrice.predefinita': () => t('Ha riportato i permessi come di serie, in tutte le organizzazioni'),
  'org.dns.link': (v) => t('Ha collegato il dominio delle istanze {dominio}', { dominio: campo(v, 'domain') }),
  'org.dns.unlink': () => t('Ha scollegato il dominio delle istanze'),
  'org.enterprise.link': () => t('Ha collegato la credenziale Enterprise'),
  'org.enterprise.unlink': () => t('Ha scollegato la credenziale Enterprise'),
  'org.continuity.targets': (v) => {
    const d = snapshot(v.after);
    return typeof d.rto_minutes === 'number'
      ? t('Ha cambiato gli obiettivi di continuità (RTO {rto} min, RPO {rpo} min)', { rto: d.rto_minutes, rpo: String(d.rpo_minutes) })
      : t('Ha cambiato gli obiettivi di continuità');
  },
  'credential.create': (v) => {
    const nome = virgolette(campo(v, 'label'));
    const p = campo(v, 'provider');
    return p
      ? t('Ha collegato il progetto {nome} su {provider}', { nome, provider: provider(p) })
      : t('Ha collegato il progetto {nome}', { nome });
  },
  'credential.delete': (v) => t('Ha scollegato il progetto {nome}', { nome: virgolette(campo(v, 'label')) }),
  'credential.rotate': (v) => t('Ha aggiornato le chiavi del progetto {nome}', { nome: virgolette(campo(v, 'label')) }),
  'alert.channel.add': (v) => t('Ha aggiunto il recapito {nome} per gli avvisi', { nome: virgolette(campo(v, 'label')) }),
  'alert.channel.remove': (v) => t('Ha tolto il recapito {nome} degli avvisi', { nome: virgolette(campo(v, 'label')) }),
};

/** La frase di una riga. Un codice sconosciuto si mostra com'è, con il nome accanto. */
export function descriviVoce(v: Voce): string {
  const nome = nomeVoce(v);
  const frase = FRASI[v.action];
  if (!frase) return nome ? `${v.action} · ${virgolette(nome)}` : v.action;
  return frase(v, virgolette(nome)).replace(/\s+/g, ' ').trim();
}

/**
 * Le famiglie con cui si filtra: ognuna è un gruppo di prefissi di azione,
 * perché «persone» sono inviti, membri, accessi e token insieme.
 */
export const FAMIGLIE: Array<{ chiave: string; label: string; prefissi: string[] }> = [
  { chiave: 'server', label: 'Server', prefissi: ['server.', 'agent.', 'ip.'] },
  { chiave: 'istanze', label: 'Istanze', prefissi: ['instance.', 'staging.', 'domain.'] },
  { chiave: 'backup', label: 'Backup', prefissi: ['backup.'] },
  { chiave: 'continuita', label: 'Continuità', prefissi: ['dr.', 'org.continuity'] },
  { chiave: 'persone', label: 'Persone', prefissi: ['invitation.', 'member.', 'grant.', 'token.', 'impersonation.', 'permessi.'] },
  { chiave: 'organizzazione', label: 'Organizzazione', prefissi: ['org.', 'credential.', 'alert.', 'mcp.'] },
];

/** «Oggi», «Ieri», o «giovedì 18 settembre» (con l'anno se non è questo). */
export function giornoDi(iso: string, adesso = new Date()): string {
  const d = new Date(iso);
  const stesso = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (stesso(d, adesso)) return t('Oggi');
  const ieri = new Date(adesso);
  ieri.setDate(ieri.getDate() - 1);
  if (stesso(d, ieri)) return t('Ieri');
  const conAnno = d.getFullYear() !== adesso.getFullYear();
  const s = d.toLocaleDateString(localeIntl(), { weekday: 'long', day: 'numeric', month: 'long', ...(conAnno ? { year: 'numeric' } : {}) });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** L'ora di una riga: «14:32». */
export function oraDi(iso: string): string {
  return new Date(iso).toLocaleTimeString(localeIntl(), { hour: '2-digit', minute: '2-digit' });
}

/** Le righe raggruppate per giorno, nell'ordine in cui arrivano (dalla più recente). */
export function perGiorno(voci: Voce[], adesso = new Date()): Array<{ giorno: string; voci: Voce[] }> {
  const gruppi: Array<{ giorno: string; voci: Voce[] }> = [];
  for (const v of voci) {
    const giorno = giornoDi(v.at, adesso);
    const ultimo = gruppi[gruppi.length - 1];
    if (ultimo && ultimo.giorno === giorno) ultimo.voci.push(v);
    else gruppi.push({ giorno, voci: [v] });
  }
  return gruppi;
}
